import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import axios from 'axios'
import { useUserStore } from '@/store/user'
import { useMemberStore } from '@/store/member'
import { portalRoutes } from './portal_routes'
import { ElMessage } from 'element-plus'

declare module 'vue-router' {
  interface RouteMeta {
    title?: string
    icon?: string
    hidden?: boolean
    public?: boolean
    /** 需要的权限码（缺省 = 仅需登录） */
    permission?: string | string[]
    /** 子路由合并时是否参与菜单展示 */
    hideInMenu?: boolean
  }
}

const routes: Array<RouteRecordRaw> = [
  ...portalRoutes,
  {
    path: '/admin/login',
    name: 'AdminLogin',
    component: () => import('@/views/login/index.vue'),
    meta: { public: true, hidden: true, isAdmin: true }
  },
  {
    path: '/403',
    name: '403',
    component: () => import('@/views/error/403.vue'),
    meta: { public: true, hidden: true, title: '403 无权限' }
  },
  // ───── 后台：单一 layout 父路由，所有页面作为 children ─────
  // 之前每个模块各自 component: layout/index.vue，跨模块跳转会卸载/重挂整个
  // layout（连带 NoticeBell 重新 onMounted 立即请求 unread-count）。
  // 收敛为单一父路由后 layout 只挂载一次，切页只换内部 <router-view>。
  // 注意：所有叶子的完整路径保持不变（菜单 menuTree 用的是硬编码 fullPath）。
  {
    path: '/admin',
    component: () => import('@/layout/index.vue'),
    redirect: '/admin/dashboard',
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/views/dashboard/index.vue'),
        meta: { title: '仪表盘', icon: 'Odometer', permission: ['page:dashboard', 'page:org_dashboard', 'page:agent_dashboard'] }
      },
      {
        path: 'users/index',
        name: 'Users',
        component: () => import('@/views/users/index.vue'),
        meta: { title: '用户管理', icon: 'UserFilled', permission: 'page:users' }
      },
      {
        path: 'debts/index',
        name: 'Debts',
        component: () => import('@/views/debts/index.vue'),
        meta: { title: '案件管理', icon: 'Document', permission: ['page:debts', 'page:org_debts', 'page:my_tasks'] }
      },
      {
        path: 'mediators/index',
        name: 'Mediators',
        component: () => import('@/views/mediators/index.vue'),
        meta: { title: '经办人管理', icon: 'Avatar', permission: ['page:mediators', 'page:org_mediators'] }
      },
      {
        path: 'applications',
        name: 'Applications',
        component: () => import('@/views/applications/index.vue'),
        meta: { title: '审核管理', icon: 'Document', permission: 'page:applications' }
      },
      {
        path: 'ecology/agents',
        name: 'Agents',
        component: () => import('@/views/ecology/agents.vue'),
        meta: { title: '代理商管理', permission: 'page:agents' }
      },
      {
        path: 'ecology/agent-regions',
        name: 'AgentRegions',
        component: () => import('@/views/ecology/agent-regions.vue'),
        meta: { title: '代理区域管理', permission: 'page:agent_regions' }
      },
      {
        path: 'ecology/agent-map',
        name: 'AgentMap',
        component: () => import('@/views/ecology/agent-map.vue'),
        // 全国代理分布图仅平台管理员可见 (代理无 page:agents); 代理看本级"代理区域管理"即可
        meta: { title: '代理区域分布', permission: 'page:agents' }
      },
      {
        path: 'ecology/user-map',
        name: 'UserMap',
        component: () => import('@/views/ecology/user-map.vue'),
        // 用户区域分布 (按发布人地区聚合), 用 page:users 权限
        meta: { title: '用户区域分布', permission: 'page:users' }
      },
      {
        path: 'ecology/my-earnings',
        name: 'MyEarnings',
        component: () => import('@/views/ecology/my-earnings.vue'),
        meta: { title: '我的收益', permission: 'page:agent_regions' }
      },
      {
        path: 'ecology/partners',
        name: 'Partners',
        component: () => import('@/views/ecology/partners.vue'),
        meta: { title: '合伙人管理', permission: 'page:partners' }
      },
      {
        path: 'finance/transactions',
        name: 'FinanceTransactions',
        component: () => import('@/views/finance/transactions.vue'),
        meta: { title: '交易订单流水', permission: 'page:finance' }
      },
      {
        path: 'finance/vouchers',
        name: 'Vouchers',
        component: () => import('@/views/finance/vouchers.vue'),
        meta: { title: '发布券明细', permission: 'page:finance' }
      },
      {
        path: 'finance/user-wallet/:userId(\\d+)',
        name: 'UserWallet',
        component: () => import('@/views/finance/user-wallet.vue'),
        meta: { title: '用户钱包流水', permission: 'page:finance', hideInMenu: true }
      },
      {
        path: 'finance/withdraws',
        name: 'Withdraws',
        component: () => import('@/views/finance/withdraws.vue'),
        meta: { title: '提现审核', permission: 'page:finance' }
      },
      {
        path: 'orgs/index',
        name: 'Orgs',
        component: () => import('@/views/orgs/index.vue'),
        meta: { title: '机构管理', icon: 'OfficeBuilding', permission: 'page:orgs' }
      },
      {
        path: 'creditors/index',
        name: 'Creditors',
        component: () => import('@/views/creditors/index.vue'),
        meta: { title: '债权人档案', icon: 'Files', permission: 'page:creditors' }
      },
      {
        path: 'system/config',
        name: 'Config',
        component: () => import('@/views/system/config.vue'),
        meta: { title: '全局配置', permission: 'page:system' }
      },
      {
        path: 'system/banners',
        name: 'Banners',
        component: () => import('@/views/system/banners.vue'),
        meta: { title: '首页轮播图', permission: 'page:system' }
      },
      {
        path: 'system/categories',
        component: () => import('@/views/system/CategoriesLayout.vue'),
        redirect: { name: 'CategoriesCase' },
        meta: { title: '字典分类', permission: 'page:system' },
        children: [
          {
            path: 'case',
            name: 'CategoriesCase',
            component: () => import('@/views/system/category-tabs/CaseTab.vue'),
            meta: { title: '案件类型', permission: 'page:system' }
          },
          {
            path: 'org',
            name: 'CategoriesOrg',
            component: () => import('@/views/system/category-tabs/OrgTab.vue'),
            meta: { title: '机构入驻类型', permission: 'page:system' }
          },
          {
            path: 'mediator',
            name: 'CategoriesMediator',
            component: () => import('@/views/system/category-tabs/MediatorTab.vue'),
            meta: { title: '经办人类型', permission: 'page:system' }
          },
          {
            path: 'info',
            name: 'CategoriesInfo',
            component: () => import('@/views/system/category-tabs/InfoTab.vue'),
            meta: { title: '行业信息分类', permission: 'page:system' }
          }
        ]
      },
      {
        path: 'system/announcements',
        name: 'Announcements',
        component: () => import('@/views/system/announcements.vue'),
        meta: { title: '公告管理', permission: 'page:system' }
      },
      {
        path: 'system/tasks',
        name: 'Tasks',
        component: () => import('@/views/system/tasks/index.vue'),
        meta: { title: '定时任务', permission: 'page:system:tasks' }
      },
      {
        path: 'messages',
        name: 'Messages',
        component: () => import('@/views/messages/index.vue'),
        meta: { title: '消息中心', icon: 'Message', permission: 'page:messages' }
      },
      {
        path: 'my-messages/index',
        name: 'MyMessages',
        component: () => import('@/views/my-messages/index.vue'),
        meta: { title: '我的消息', hideInMenu: true }
      },
      {
        path: 'levels/member',
        name: 'MemberLevels',
        component: () => import('@/views/levels/member.vue'),
        meta: { title: '会员等级', permission: 'page:levels' }
      },
      {
        path: 'levels/partner',
        name: 'PartnerLevels',
        component: () => import('@/views/levels/partner.vue'),
        meta: { title: '合伙人等级', permission: 'page:levels' }
      },
      // ───── 管理员账号 + RBAC + 审计日志 ─────
      {
        path: 'admin-users/index',
        name: 'AdminUsers',
        component: () => import('@/views/admin-users/index.vue'),
        meta: { title: '管理员账号', icon: 'Avatar', permission: 'page:admin_users' }
      },
      {
        path: 'rbac/roles',
        name: 'Roles',
        component: () => import('@/views/rbac/roles.vue'),
        meta: { title: '角色管理', permission: 'page:roles' }
      },
      {
        path: 'rbac/permissions',
        name: 'Permissions',
        component: () => import('@/views/rbac/permissions.vue'),
        meta: { title: '权限码', permission: 'page:permissions' }
      },
      {
        path: 'audit-logs/index',
        name: 'AuditLogs',
        component: () => import('@/views/audit-logs/index.vue'),
        meta: { title: '操作日志(后台)', icon: 'Document', permission: 'page:audit_logs' }
      },
      {
        path: 'audit-logs/member-logs',
        name: 'MemberLogs',
        component: () => import('@/views/member-logs/index.vue'),
        meta: { title: '操作日志(用户)', icon: 'User', permission: 'page:audit_logs' }
      },
      // 后台专属 404 兜底: 必须放在 children 末尾。
      // 缺这条时, 任何拼错的 /admin/xxx 都会落进 portal_routes 的全局
      // ':pathMatch(.*)*' → 渲染前台 404 页, 后台用户直接跳出 layout, 极易误判为接口挂了。
      {
        path: ':pathMatch(.*)*',
        name: 'AdminNotFound',
        component: () => import('@/views/error/404.vue'),
        meta: { title: '页面不存在', hideInMenu: true }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

/**
 * token 校验结果三态。
 *
 * 旧实现只返回 boolean, 把"后端暂时不可用"也当成 true, 调用方紧接着
 * markTokenVerified() 永久标记为已验证 —— 但此时 adminInfo 根本没拿到,
 * permissions 是空集, 之后每个带 permission 的路由都判为无权限 → 跳 /403
 * → 用户点"重新登录"清 token, 整个会话报废 (经办人"莫名掉登录"的根因)。
 *
 * 拆成三态后: 只有 'ok' 才允许标记已验证, 'unavailable' 留待下次导航重试。
 */
type VerifyResult = 'ok' | 'unauthorized' | 'unavailable'

async function verifyTokenWithBackend(token: string): Promise<VerifyResult> {
  try {
    const res = await axios.get('/api/admin/auth/me', {
      headers: { Authorization: `Bearer ${token}` },
      // /auth/me 还会连带查 Agents / MediationOrgs, 5s 在弱网或高负载下很容易打满
      timeout: 10000
    })
    const data = res.data?.data
    if (!data) {
      // 200 但没有 body: 视为后端异常, 不能当作验证通过
      return 'unavailable'
    }
    // 把后端返回的 admin info 存进 store (含 permissions)
    const userStore = useUserStore()
    userStore.setAdminInfo(data)
    return 'ok'
  } catch (err: unknown) {
    const error = err as { response?: { status: number } }
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      return 'unauthorized'
    }
    // 超时 / 502 / 504 / 网络抖动 / 后端重启: 不登出, 也不标记已验证
    return 'unavailable'
  }
}

router.beforeEach(async (to, _from, next) => {
  const isPortal = to.matched.some(record => record.meta.isPortal)

  if (isPortal) {
    // Portal 前台逻辑
    const memberStore = useMemberStore()
    const isLoggedIn = !!memberStore.token

    if (to.meta.public) {
      // /login 直接放行, 不因有 token 就跳走 —— 与 admin 登录页一致, 允许切换账号 / 重新登录。
      // 登录后访问首页，重定向到个人中心 (产品需求)
      if (isLoggedIn && to.path === '/') {
        return next({ path: '/profile' })
      }
      return next()
    }

    if (!isLoggedIn) {
      return next({ path: '/login', query: { redirect: to.fullPath } })
    }

    // Portal 登录后直接放行
    return next()
  } else {
    // Admin 后台逻辑
    const userStore = useUserStore()
    const isLoggedIn = !!userStore.token

    if (to.meta.public) {
      // 登录页直接放行, 不因有 token 就跳走 —— 用户可能是切换账号 / token 已过期想重新登录。
      // 旧逻辑 "有 token 就跳 dashboard" 会导致: token 过期 → 跳 dashboard → 401 → 跳回 login,
      // 形成肉眼可见的闪烁, 且剥夺了用户主动登录的入口。
      return next()
    }

    if (!isLoggedIn) {
      return next({ path: '/admin/login', query: { redirect: to.fullPath } })
    }

    // 软验证 token + 拉 admin info
    if (!userStore.tokenVerified) {
      const result = await verifyTokenWithBackend(userStore.token)
      if (result === 'unauthorized') {
        userStore.logout()
        return next({ path: '/admin/login', query: { redirect: to.fullPath } })
      }
      // 只有真正拿到 adminInfo 才算验证通过; 'unavailable' 时保持未验证状态,
      // 下次导航会自动重试, 不会把用户永久钉死在空权限状态。
      if (result === 'ok') {
        userStore.markTokenVerified()
      }
    }

    // 权限校验
    // 前置条件: 必须已经拿到 adminInfo。permissions 为空既可能是"真的没权限",
    // 也可能是"还没拉到" —— 后者若按无权限处理就会把人踢去 /403。
    // 拉不到时放行, 由后端 requires_permission 兜底 (后端才是安全边界,
    // 前端这层只是 UX 优化, 越权请求会被后端 403 并由拦截器提示"权限不足")。
    const required = to.meta.permission
    if (required && userStore.adminInfo && !userStore.hasPermission(required)) {
      if (to.path === '/403') return next()

      ElMessage.error('没有该页面的访问权限')

      // 三类角色各有自己的 dashboard 权限码, 任一命中即可退回仪表盘
      const dashboardPerms = ['page:dashboard', 'page:org_dashboard', 'page:agent_dashboard']
      if (to.path === '/admin/dashboard' || !userStore.hasPermission(dashboardPerms)) {
        return next({ path: '/403' })
      }
      return next({ path: '/admin/dashboard' })
    }

    return next()
  }
})

export default router